import type { Chiave, Traduttore } from "../../i18n/index.ts";
import type { Issue, IssueCode } from "./types.ts";

/**
 * Traduzione di un problema di validazione nel gesto che lo risolve.
 *
 * È la differenza fra sapere che qualcosa non va e poterlo sistemare: senza
 * questo, leggere «la variabile DATABASE_URL non ha un valore» costringe a
 * cambiare scheda, ricordare il nome e cercare il punto a mano.
 */

export type Azione =
  /** Apre l'environment con la variabile già aggiunta, pronta da compilare. */
  | { readonly tipo: "imposta-variabile"; readonly etichetta: Chiave; readonly variabile: string }
  /** Apre il compose e porta alla riga che contiene il termine. */
  | { readonly tipo: "cerca-nel-compose"; readonly etichetta: Chiave; readonly termine: string }
  /** Apre l'environment filtrato sulla variabile, per correggerla o rimuoverla. */
  | { readonly tipo: "filtra-variabile"; readonly etichetta: Chiave; readonly variabile: string };

/**
 * Termine con cui trovare un servizio nel compose.
 *
 * `nome:` è la sua chiave, quindi porta esattamente alla riga che lo dichiara,
 * mentre cercare il solo nome finirebbe sulla prima menzione qualsiasi — spesso
 * un riferimento in `depends_on`.
 */
const chiaveServizio = (servizio: string) => `${servizio}:`;

/**
 * Azione adatta a un problema, se esiste.
 *
 * `immagine` è quella del servizio coinvolto, quando la si conosce: serve a
 * distinguere il suo `image:` da quello degli altri servizi. Restituisce `null`
 * quando non c'è un gesto sensato da proporre: un pulsante che non porta dove
 * serve è peggio della sua assenza.
 */
export function azionePer(problema: Issue, immagine?: string | null): Azione | null {
  const codice = problema.code as IssueCode;

  if (codice === "MISSING_VARIABLE" && problema.variable) {
    return {
      tipo: "imposta-variabile",
      etichetta: "verifica.impostaVariabile",
      variabile: problema.variable,
    };
  }

  if (codice === "UNUSED_VARIABLE" && problema.variable) {
    return {
      tipo: "filtra-variabile",
      etichetta: "verifica.mostraEnvironment",
      variabile: problema.variable,
    };
  }

  if (problema.service) {
    // Per i problemi del tag si cerca l'immagine stessa: `image:` cadrebbe sulla
    // prima del file, che con più servizi è quasi sempre quella sbagliata. Senza
    // l'immagine si ricade sulla riga che dichiara il servizio, che è comunque
    // vicina al punto da correggere.
    const problemaDiTag = codice === "MISSING_TAG" || codice === "MUTABLE_TAG";
    const cercaImmagine = problemaDiTag && Boolean(immagine);

    return {
      tipo: "cerca-nel-compose",
      etichetta: cercaImmagine ? "verifica.apriImmagine" : "verifica.apriNelCompose",
      termine: cercaImmagine ? immagine! : chiaveServizio(problema.service),
    };
  }

  return null;
}

/** Spiegazione breve dell'effetto pratico di un problema. */
export function conseguenzaDi(codice: IssueCode, t: Traduttore): string {
  return t.traduci({ chiave: `verifica.conseguenza.${codice}` }) ?? "";
}
